import React, { memo, useMemo, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  Tooltip,
  Button,
} from '@mui/material';
import {
  History as HistoryIcon,
  Map as MapIcon,
  Restore as RestoreIcon,
} from '@mui/icons-material';
import LiveMap from './LiveMap';

const MapMountManager = (() => {
  const MAX_ACTIVE = 2;
  const activeList = [];
  const registry = new Map();

  function acquireSlot(id, unmountFn) {
    const existingIndex = activeList.findIndex(entry => entry.id === id);
    if (existingIndex !== -1) {
      registry.set(id, unmountFn);
      activeList[existingIndex].unmount = unmountFn;
      return true;
    }

    if (activeList.length < MAX_ACTIVE) {
      registry.set(id, unmountFn);
      activeList.push({ id, unmount: unmountFn });
      return true;
    }

    const evicted = activeList.shift();
    if (evicted && typeof evicted.unmount === 'function') {
      try {
        evicted.unmount();
      } catch (_) {}
    }
    registry.delete(evicted?.id);

    registry.set(id, unmountFn);
    activeList.push({ id, unmount: unmountFn });
    return true;
  }

  function releaseSlot(id) {
    registry.delete(id);
    const index = activeList.findIndex(entry => entry.id === id);
    if (index !== -1) {
      activeList.splice(index, 1);
    }
  }

  return { acquireSlot, releaseSlot };
})();

const MobileLiveMapGate = memo(function MobileLiveMapGate({
  renderMap,
  placeholderTitle = 'Interactive Map',
}) {
  const containerRef = useRef(null);
  const [isShown, setIsShown] = useState(false);
  const [isInView, setIsInView] = useState(true);
  const instanceIdRef = useRef(
    `map_gate_${Math.random().toString(36).slice(2)}`,
  );

  useEffect(() => {
    return () => {
      MapMountManager.releaseSlot(instanceIdRef.current);
    };
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        const visible = entry.isIntersecting && entry.intersectionRatio > 0;
        setIsInView(visible);
      },
      { root: null, rootMargin: '0px', threshold: [0, 0.01, 0.1] },
    );
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Auto mount when entering view (respecting global limit). Eviction only via manager.
  useEffect(() => {
    if (isInView && !isShown) {
      const ok = MapMountManager.acquireSlot(instanceIdRef.current, () => {
        setIsShown(false);
      });
      if (ok) setIsShown(true);
    }
    // Note: We don't release when out of view - manager handles eviction
  }, [isInView, isShown]);

  return (
    <Box
      ref={containerRef}
      sx={{
        marginBottom: '1rem',
        marginTop: '1rem',
        maxWidth: '100%',
        width: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      {!isShown ? (
        <Box
          sx={{
            p: 2,
            border: '1px solid rgba(64,196,170,0.3)',
            borderRadius: '8px',
            background: 'rgba(64,196,170,0.06)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MapIcon sx={{ color: '#40c4aa' }} />
            <Typography
              variant='body2'
              sx={{ color: '#39585C', fontWeight: 600 }}
            >
              {placeholderTitle}
            </Typography>
          </Box>
        </Box>
      ) : (
        renderMap()
      )}
    </Box>
  );
});

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia && window.matchMedia('(max-width: 767px)').matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = () => setIsMobile(mq.matches);
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, []);
  return isMobile;
}

// Component for clickable artifact version links
const ArtifactVersionLink = memo(function ArtifactVersionLink({
  version,
  title,
  isCurrentVersion,
  onLoadVersion,
}) {
  const handleClick = () => {
    if (!isCurrentVersion && onLoadVersion) {
      onLoadVersion(version - 1); // Convert to 0-based index
    }
  };

  return (
    <Tooltip
      title={
        isCurrentVersion
          ? 'This is the current version'
          : `Click to load version ${version}: ${title || 'Artifact'}`
      }
    >
      <Chip
        icon={<HistoryIcon />}
        label={`v${version}`}
        size='small'
        variant={isCurrentVersion ? 'filled' : 'outlined'}
        color={isCurrentVersion ? 'primary' : 'default'}
        clickable={!isCurrentVersion}
        onClick={handleClick}
        sx={{
          margin: '0 4px',
          cursor: isCurrentVersion ? 'default' : 'pointer',
          '&:hover': {
            backgroundColor: isCurrentVersion
              ? undefined
              : 'rgba(64, 196, 170, 0.08)',
          },
        }}
      />
    </Tooltip>
  );
});

// Component for restoring specific artifact versions from messages
const ArtifactRestoreButton = memo(function ArtifactRestoreButton({
  artifactData,
  onRestoreArtifact,
}) {
  const handleRestore = () => {
    if (artifactData && onRestoreArtifact) {
      onRestoreArtifact(artifactData);
    }
  };

  const artifactTitle =
    artifactData?.title || artifactData?.mapTitle || 'Artifact';

  return (
    <Tooltip title={`Restore and view this artifact: ${artifactTitle}`}>
      <Button
        size='small'
        variant='outlined'
        color='primary'
        startIcon={<RestoreIcon />}
        onClick={handleRestore}
        sx={{
          marginLeft: '0.5rem',
          textTransform: 'none',
          borderColor: '#40c4aa',
          color: '#40c4aa',
          '&:hover': {
            borderColor: '#359a85',
            backgroundColor: 'rgba(64, 196, 170, 0.08)',
          },
        }}
      >
        View Artifact
      </Button>
    </Tooltip>
  );
});

// Function to check if text contains artifact references
function hasArtifactReferences(text) {
  const artifactPatterns = [
    /\b(artifact|version|field\s+map|map)\b/gi,
    /\b(previous|earlier|initial|first|latest|current)\s+(version|artifact|map)\b/gi,
    /\b(version|artifact)\s+(\d+)\b/gi,
  ];

  return artifactPatterns.some(pattern => pattern.test(text));
}

function extractMarkdownSegments(text) {
  const segments = [];

  // Remove non-code tags first
  const cleaned = text.replace(/\[((?!code)[^[\]]+)\](.*?)\[\/\1\]/g, '$2');
  const parts = cleaned.split(/(\[code\][\s\S]*?\[\/code\])/g);

  for (const part of parts) {
    const trimmed = part.trim();
    if (!trimmed) continue;

    if (trimmed.startsWith('[code]') && trimmed.endsWith('[/code]')) {
      const codeContent = trimmed.slice(6, -7).trim();
      const mapSegment = parseAsMapInfo(codeContent);

      if (mapSegment) {
        segments.push(mapSegment);
      } else {
        segments.push({
          type: 'markdown',
          content: `\`\`\`\n${codeContent}\n\`\`\``,
        });
      }
    } else {
      // Check for legacy mapID outside code blocks
      const mapSegment = parseAsMapInfo(trimmed);
      if (mapSegment) {
        segments.push(mapSegment);
      } else {
        segments.push({ type: 'markdown', content: trimmed });
      }
    }
  }

  return segments;
}

function parseAsMapInfo(content) {
  console.log('Processing content:', content);

  // Try structured parsing first (JSON or JS object)
  const structuredResult = tryStructuredParsing(content);
  if (structuredResult) {
    console.log('Parsed as structured object:', structuredResult);
    return { type: 'map', mapInfo: structuredResult };
  }

  // Try regex patterns for mapID
  const regexResult = tryRegexParsing(content);
  if (regexResult !== undefined) {
    console.log('Parsed with regex, mapID:', regexResult);
    return { type: 'map', mapInfo: { mapID: regexResult, mapBlock: null } };
  }

  return null;
}

function tryStructuredParsing(content) {
  if (!content.startsWith('{') || !content.endsWith('}')) {
    return null;
  }

  // Try JSON parsing first
  try {
    const json = JSON.parse(content);
    if (json && typeof json === 'object' && 'mapID' in json) {
      return json;
    }
  } catch (e) {
    console.log('JSON parse failed, trying JS object literal');
  }

  // Try Function constructor
  try {
    const funcBody = `"use strict"; return (${content});`;
    const obj = new Function(funcBody)();
    if (obj && typeof obj === 'object' && obj.mapID) {
      return obj;
    }
  } catch (err) {
    console.log('Function constructor failed:', err.message);
  }

  // Try eval as last resort
  try {
    const obj = eval(`(${content})`);
    if (obj && typeof obj === 'object' && obj.mapID) {
      return obj;
    }
  } catch (err) {
    console.log('Eval parse failed:', err.message);
  }

  return null;
}

function tryRegexParsing(content) {
  // Pattern 1: mapID: "value" or mapID: 'value'
  const colonMatch = content.match(/mapID\s*:\s*["']([^"']+)["']/);
  if (colonMatch) {
    return colonMatch[1];
  }

  // Pattern 2: mapID=value (legacy format)
  const equalsMatch = content.match(/^mapID\s*=\s*([a-zA-Z0-9]+)/);
  if (equalsMatch) {
    return equalsMatch[1];
  }

  // Pattern 3: mapID=[] or mapID="" (empty values)
  const emptyMatch = content.match(/^mapID\s*=\s*(?:\[\s*\]|["']{2})/);
  if (emptyMatch) {
    return null;
  }

  return undefined; // Explicitly return undefined when no match found
}

const MarkdownRenderer = memo(function MarkdownRenderer({
  text,
  addressInfo,
  token,
  artifactHistory = [],
  currentArtifactIndex = 0,
  onLoadArtifactVersion,
  // New props for message-specific artifact restoration
  messageData = null,
  onRestoreMessageArtifact,
}) {
  const segments = useMemo(() => {
    const result = extractMarkdownSegments(text);
    console.log('Final segments:', result);
    return result;
  }, [text]);

  const isMobile = useIsMobile();

  // const showArtifactVersions = useMemo(() => {
  //   return hasArtifactReferences(text) && artifactHistory.length > 1;
  // }, [text, artifactHistory]);

  const showMessageArtifactRestore = useMemo(() => {
    return (
      messageData &&
      (messageData.type === 'artifact_color_change' ||
        messageData.type === 'artifact_field_display') &&
      messageData.artifactData &&
      onRestoreMessageArtifact
    );
  }, [messageData, onRestoreMessageArtifact]);

  return (
    <Box
      sx={{
        maxWidth: '100%',
        lineHeight: 1.5,
        color: '#757575',
      }}
    >
      {segments.map((segment, index) => {
        if (segment.type === 'map' && segment.mapInfo) {
          const { mapID, mapBlock } = segment.mapInfo;

          // If neither a webmapID nor a selected block is provided, show placeholder
          if (!mapID && !mapBlock) {
            return (
              <Box
                key={index}
                sx={{
                  marginBottom: '1rem',
                  marginTop: '1rem',
                  padding: '1rem',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '4px',
                  textAlign: 'center',
                }}
              >
                <Typography variant='body1' color='text.secondary'>
                  No map available.
                </Typography>
              </Box>
            );
          }

          const isValidMapID =
            mapID &&
            mapID !== 'none' &&
            mapID !== '[none]' &&
            mapID.trim() !== '' &&
            mapID !== '[ ]' &&
            mapID !== '[]' &&
            mapID !== '[No specific mapID provided]' &&
            mapID !== 'null' &&
            mapID !== 'undefined';

          // If relying on webmapID but it's invalid, show placeholder
          if (!mapBlock && !isValidMapID) {
            return (
              <Box
                key={index}
                sx={{
                  marginBottom: '1rem',
                  marginTop: '1rem',
                  padding: '1rem',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '4px',
                  textAlign: 'center',
                }}
              >
                <Typography variant='body1' color='text.secondary'>
                  No map available.
                </Typography>
              </Box>
            );
          }

          // Desktop: render map immediately (unchanged behavior)
          if (!isMobile) {
            return (
              <Box
                key={index}
                sx={{
                  marginBottom: '1rem',
                  marginTop: '1rem',
                  maxWidth: '100%',
                  width: '100%',
                  boxSizing: 'border-box',
                  overflow: 'hidden',
                }}
              >
                {process.env.REACT_APP_DEV_MODE !== 'false' && (
                  <LiveMap
                    webmapID={mapID}
                    token={token}
                    addressInfo={addressInfo}
                    defaultSettings={!mapBlock}
                    selectedBlock={mapBlock}
                    mapInfo={segment.mapInfo}
                  />
                )}
              </Box>
            );
          }

          // Mobile: gate behind button and auto-destroy when out of view
          return (
            <MobileLiveMapGate
              key={index}
              placeholderTitle={segment.mapInfo?.title || 'Interactive Map'}
              renderMap={() =>
                process.env.REACT_APP_DEV_MODE !== 'false' && (
                  <LiveMap
                    webmapID={mapID}
                    token={token}
                    addressInfo={addressInfo}
                    defaultSettings={!mapBlock}
                    selectedBlock={mapBlock}
                    mapInfo={segment.mapInfo}
                  />
                )
              }
            />
          );
        }

        // Markdown segment
        return (
          <ReactMarkdown
            key={index}
            rehypePlugins={[rehypeRaw]}
            remarkPlugins={[remarkGfm]}
            components={{
              h3: ({ node, ...props }) => (
                <h3 {...props} style={{ textAlign: 'left' }} />
              ),
              a: ({ node, ...props }) => (
                <a
                  {...props}
                  target='_blank'
                  rel='noopener noreferrer'
                  style={{
                    color: '#40c4aa',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                />
              ),
              table: ({ node, ...props }) => (
                <Paper sx={{ overflow: 'auto', margin: '1rem 0' }}>
                  <Table size='small' {...props} />
                </Paper>
              ),
              thead: ({ node, ...props }) => <TableHead {...props} />,
              tbody: ({ node, ...props }) => <TableBody {...props} />,
              tr: ({ node, ...props }) => <TableRow {...props} />,
              th: ({ node, ...props }) => (
                <TableCell
                  {...props}
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: '#f5f5f5',
                    color: '#424242',
                  }}
                />
              ),
              td: ({ node, ...props }) => <TableCell {...props} />,
            }}
          >
            {segment.content}
          </ReactMarkdown>
        );
      })}

      {/* Message-Specific Artifact Restore Button */}
      {showMessageArtifactRestore && (
        <Box
          sx={{
            marginTop: '1rem',
            padding: '0.75rem',
            backgroundColor: 'rgba(64, 196, 170, 0.06)',
            borderRadius: '8px',
            borderLeft: '3px solid #40c4aa',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Typography
              variant='body2'
              sx={{
                color: '#39585C',
                fontWeight: 600,
                marginBottom: '0.25rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
              }}
            >
              <MapIcon fontSize='small' />
              Artifact Available
            </Typography>
            <Typography
              variant='caption'
              sx={{
                color: '#666',
              }}
            >
              This message created an artifact version that you can restore and
              view.
            </Typography>
          </Box>
          <ArtifactRestoreButton
            artifactData={messageData.artifactData}
            onRestoreArtifact={onRestoreMessageArtifact}
          />
        </Box>
      )}
    </Box>
  );
});

export default MarkdownRenderer;
